/**
 * AppInit - app initialization and node menu data
 *
 * Handles audio context, RuntimeStore initialization, and provides
 * primitive/library info for the node menu.
 */

import { createContext, useContext, useState, useEffect, useRef, type ReactNode } from 'react';
import { webRuntimeApi } from '@/runtime/web';

import {
  initWebAudioContext,
  resumeAudioContext,
  isAudioContextReady
} from '@/env/web/AudioContext';
import { logger } from '@/logging';
import { MenuState } from '@/runtime/RuntimeStore';
import { createRegisterUIContext } from '@/ui/components/node/VisualizationRegistry';



interface AppInitValue {
  loading: boolean;
  error: string | null;
  // Node menu data
  menu: MenuState
}

const AppInitContext = createContext<AppInitValue | null>(null);

export function AppInitProvider({ children }: { children: ReactNode }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [menu, setMenu] = useState<MenuState>();
  const audioInitialized = useRef(false);

  // Initialize audio context and subscribe to RuntimeStore ready event
  useEffect(() => {
    let cancelled = false;

    // Initialize audio context
    const initAudio = async () => {
      try {
        logger.info('App', 'Initializing audio context...');
        await initWebAudioContext();
        if (cancelled) return;
        logger.info('App', 'Audio context initialized');
      } catch (err) {
        if (!cancelled) {
          const error = err instanceof Error ? err : new Error(String(err));
          logger.error('App', `Failed to initialize audio: ${error.message}`);
          setError(String(err));
        }
      }
    };

    // Register plugin UI components (visualizations, etc.)
    const initPluginUI = async () => {
      const plugins = webRuntimeApi.getPluginRegistry().getAllPlugins();
      for (const plugin of plugins) {
        if (plugin.registerUI) {
          try {
            const context = createRegisterUIContext(plugin.namespace);
            await plugin.registerUI(context);
            logger.trace('App', `Registered UI for plugin: ${plugin.id}`);
          } catch (err) {
            logger.error('App', `Failed to register UI for plugin ${plugin.id}:`, err);
          }
        }
      }
    };

    initAudio();
    initPluginUI();

    // Subscribe to ready event
    const unsubscribe = webRuntimeApi.subscribe.ready((menuState) => {
      if (cancelled) return;
      logger.info('App', 'Runtime ready');
      setMenu(menuState);
      setError(null);
      setLoading(false);
    });

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, []);

  // Resume audio context on first user gesture (required by browsers)
  useEffect(() => {
    const initAudioOnGesture = async () => {
      if (audioInitialized.current || isAudioContextReady()) return;

      try {
        await resumeAudioContext();
        audioInitialized.current = true;
        logger.info('App', 'Audio initialized');
        document.removeEventListener('click', initAudioOnGesture);
        document.removeEventListener('keydown', initAudioOnGesture);
      } catch (err) {
        logger.error('App', 'Failed to initialize audio');
      }
    };

    document.addEventListener('click', initAudioOnGesture);
    document.addEventListener('keydown', initAudioOnGesture);

    return () => {
      document.removeEventListener('click', initAudioOnGesture);
      document.removeEventListener('keydown', initAudioOnGesture);
    };
  }, []);

  const value: AppInitValue = {
    loading,
    error,
    menu
  };

  return (
    <AppInitContext.Provider value={value}>
      {children}
    </AppInitContext.Provider>
  );
}

export function useAppInit(): AppInitValue {
  const context = useContext(AppInitContext);
  if (!context) {
    throw new Error('useAppInit must be used within AppInitProvider');
  }
  return context;
}
